import React, { useState, useEffect, useRef } from 'react';

// Global cache to remember images that have already loaded during the session.
// This ensures that when navigating back from Game Details or scrolling back up,
// previously loaded images display immediately in full resolution without re-blurring.
const loadedImageCache = new Set();

/**
 * ProgressiveImage component for blur-up loading.
 * Shows a blurred low-quality placeholder on FIRST load/scroll.
 * Once loaded, it remembers the image so navigating back renders instantly without blur.
 */
export default function ProgressiveImage({ src, placeholderSrc, alt, className, ...props }) {
    const isAlreadyLoaded = Boolean(src && loadedImageCache.has(src));

    const [currentSrc, setCurrentSrc] = useState(isAlreadyLoaded ? src : (placeholderSrc || src));
    const [loading, setLoading] = useState(!isAlreadyLoaded);
    const [isInView, setIsInView] = useState(isAlreadyLoaded);
    const containerRef = useRef(null);

    useEffect(() => {
        if (isAlreadyLoaded) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsInView(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '100px' }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, [isAlreadyLoaded]);

    useEffect(() => {
        if (!src) return;

        if (loadedImageCache.has(src)) {
            setCurrentSrc(src);
            setLoading(false);
            return;
        }

        if (!isInView) return;

        if (!placeholderSrc || placeholderSrc === src) {
            const img = new Image();
            img.src = src;
            img.onload = () => {
                loadedImageCache.add(src);
                setCurrentSrc(src);
                setLoading(false);
            };
            img.onerror = () => {
                setLoading(false);
            };
            return;
        }

        // Display placeholder while loading high-res image
        setCurrentSrc(placeholderSrc);
        setLoading(true);

        const img = new Image();
        img.src = src;
        img.onload = () => {
            loadedImageCache.add(src);
            setCurrentSrc(src);
            setLoading(false);
        };
        img.onerror = () => {
            // Keep placeholder if main image fails
            setCurrentSrc(placeholderSrc);
            setLoading(false);
        };
    }, [src, placeholderSrc, isInView]);

    return (
        <div ref={containerRef} className={`relative overflow-hidden ${className || ''}`} {...props}>
            <img
                src={currentSrc}
                alt={alt || ''}
                className={`w-full h-full object-cover transition-all duration-700 ease-out
                     ${loading ? 'blur-md scale-105 filter saturate-50' : 'blur-0 scale-100 filter saturate-100'}`}
                loading="lazy"
            />
            {loading && (
                <div className="absolute inset-0 bg-white/5 animate-pulse pointer-events-none" />
            )}
        </div>
    );
}
